import { DEFAULT_SCORING_CONFIG } from '@/modules/performance/hr-performance-dashboard/utils/scoring-engine';
import type { KpiConfigurationFormValues } from '../schemas/kpi-configuration-form-schema';
import type { KpiConfiguration, KpiConfigurationSaveInput } from '../types/kpi-configuration.types';

/**
 * The legacy backend only accepts exactly these reviewer groups and qualitative categories
 * (UpdateKpiConfigurationCommandValidator), so the form renders a fixed set of weights rather than
 * free-form keys. The scoring engine owns the defaults, which keeps both screens on one source.
 */
export const REVIEWER_GROUP_KEYS = Object.keys(DEFAULT_SCORING_CONFIG.reviewerGroupWeights);
export const CATEGORY_KEYS = Object.keys(DEFAULT_SCORING_CONFIG.categoryWeights);

/** Weights are stored as fractions of 1 but edited as percentages (legacy rounds to 2 / 4 decimals). */
export const fractionToPercent = (fraction = 0) => Math.round(fraction * 10000) / 100;
export const percentToFraction = (percent = 0) => Math.round((percent / 100) * 10000) / 10000;

const parseFractions = (json?: string | null): Record<string, number> => {
  if (!json) return {};
  try {
    return JSON.parse(json) as Record<string, number>;
  } catch {
    return {};
  }
};

const toPercentMap = (keys: string[], fractions: Record<string, number>) =>
  Object.fromEntries(keys.map((key) => [key, fractionToPercent(fractions[key])]));

const toFractionMap = (percents: Record<string, number>) =>
  Object.fromEntries(
    Object.entries(percents).map(([key, value]) => [key, percentToFraction(value)])
  );

/**
 * The legacy GetConfiguration serves the built-in defaults until an admin saves a configuration,
 * so a missing record is not an empty form.
 */
export const toKpiConfigurationFormValues = (
  record: KpiConfiguration | null | undefined
): KpiConfigurationFormValues => {
  if (!record) {
    return {
      QnMatrixMinLevel: DEFAULT_SCORING_CONFIG.qnMatrixMinLevel,
      QnMatrixMaxLevel: DEFAULT_SCORING_CONFIG.qnMatrixMaxLevel,
      NormalizedMaxScale: DEFAULT_SCORING_CONFIG.normalizedMaxScale,
      ReviewerGroupWeights: toPercentMap(
        REVIEWER_GROUP_KEYS,
        DEFAULT_SCORING_CONFIG.reviewerGroupWeights
      ),
      CategoryWeights: toPercentMap(CATEGORY_KEYS, DEFAULT_SCORING_CONFIG.categoryWeights),
    };
  }

  return {
    QnMatrixMinLevel: record.QnMatrixMinLevel,
    QnMatrixMaxLevel: record.QnMatrixMaxLevel,
    NormalizedMaxScale: record.NormalizedMaxScale,
    ReviewerGroupWeights: toPercentMap(
      REVIEWER_GROUP_KEYS,
      parseFractions(record.ReviewerGroupWeightsJson)
    ),
    CategoryWeights: toPercentMap(CATEGORY_KEYS, parseFractions(record.CategoryWeightsJson)),
  };
};

/** The document persisted on save; weights go back to fractions because that is what scoring reads. */
export const toKpiConfigurationInput = (
  values: KpiConfigurationFormValues
): KpiConfigurationSaveInput => ({
  QnMatrixMinLevel: values.QnMatrixMinLevel,
  QnMatrixMaxLevel: values.QnMatrixMaxLevel,
  NormalizedMaxScale: values.NormalizedMaxScale,
  ReviewerGroupWeightsJson: JSON.stringify(toFractionMap(values.ReviewerGroupWeights)),
  CategoryWeightsJson: JSON.stringify(toFractionMap(values.CategoryWeights)),
});
